// @ts-check
import { Component } from '@theme/component';

/**
 * @typedef {object} SearchModalRefs
 * @property {HTMLDialogElement} dialog - The modal dialog element.
 * @property {HTMLFormElement} form - The search form.
 * @property {HTMLInputElement} input - The search input.
 * @property {HTMLButtonElement} resetButton - The reset/clear button.
 * @property {HTMLElement} resultsContainer - The results container.
 * @property {HTMLElement} results - The results element.
 * @property {HTMLElement} loading - The loading indicator.
 * @property {HTMLElement} noResults - The no results message.
 * @property {HTMLElement} [defaultContent] - The default content shown before search.
 *
 * @extends {Component<SearchModalRefs>}
 */
class SearchModal extends Component {
  requiredRefs = ['dialog', 'form', 'input', 'resetButton', 'results', 'loading', 'noResults'];

  /** @type {AbortController | null} */
  #searchController = null;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #debounceTimer = null;

  /** @type {string} */
  #previousQuery = '';

  /** @type {Map<string, string>} */
  #cache = new Map();

  /** @type {string} */
  routesRoot = '/';

  /** @type {number} */
  resultsLimit = 4;

  connectedCallback() {
    super.connectedCallback();

    this.routesRoot = this.dataset.routesRoot || '/';
    this.resultsLimit = parseInt(this.dataset.resultsLimit || '4', 10);

    this.#bindEvents();
    this.#bindTriggers();
    this.#setupThemeEditorListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#searchController?.abort();
    if (this.#debounceTimer) clearTimeout(this.#debounceTimer);
    this.#removeThemeEditorListeners();
  }

  #bindEvents() {
    const dialog = this.refs.dialog;
    if (!dialog) return;

    dialog.addEventListener('close', () => this.#handleClose());
    dialog.addEventListener('cancel', (e) => {
      e.preventDefault();
      this.close();
    });

    // Close on backdrop click
    dialog.addEventListener('click', (e) => {
      if (e.target === dialog) {
        this.close();
      }
    });
  }

  #bindTriggers() {
    document.querySelectorAll('[data-search-trigger]').forEach((trigger) => {
      trigger.addEventListener('click', () => this.open());
    });
  }

  #setupThemeEditorListeners() {
    if (typeof Shopify === 'undefined' || !Shopify?.designMode) return;

    document.addEventListener('shopify:section:select', this.#handleSectionSelect);
    document.addEventListener('shopify:section:deselect', this.#handleSectionDeselect);
  }

  #removeThemeEditorListeners() {
    document.removeEventListener('shopify:section:select', this.#handleSectionSelect);
    document.removeEventListener('shopify:section:deselect', this.#handleSectionDeselect);
  }

  /** @param {Event} event */
  #handleSectionSelect = (event) => {
    const customEvent = /** @type {CustomEvent} */ (event);
    const eventSectionId = customEvent.detail?.sectionId;
    const sectionId = this.dataset.sectionId;

    if (eventSectionId && sectionId && eventSectionId === sectionId) {
      this.open();
    }
  };

  /** @param {Event} event */
  #handleSectionDeselect = (event) => {
    const customEvent = /** @type {CustomEvent} */ (event);
    const eventSectionId = customEvent.detail?.sectionId;
    const sectionId = this.dataset.sectionId;

    if (eventSectionId && sectionId && eventSectionId === sectionId) {
      this.close();
    }
  };

  /**
   * Opens the search modal.
   */
  open() {
    const dialog = this.refs.dialog;
    if (!dialog || dialog.open) return;

    dialog.showModal();
    document.body.style.overflow = 'hidden';

    requestAnimationFrame(() => {
      this.refs.input?.focus();
    });
  }

  /**
   * Closes the search modal.
   */
  close() {
    const dialog = this.refs.dialog;
    if (!dialog || !dialog.open) return;

    dialog.close();
  }

  #handleClose() {
    document.body.style.overflow = '';
    this.#reset();
  }

  #reset() {
    if (this.refs.input) {
      this.refs.input.value = '';
    }
    this.#previousQuery = '';
    this.#updateResetButton();
    this.#showDefaultContent();
  }

  /**
   * Handles input events on the search field.
   * @param {Event} event
   */
  handleInput(event) {
    const input = /** @type {HTMLInputElement} */ (event.target);
    const query = input.value.trim();

    this.#updateResetButton();

    if (this.#debounceTimer) {
      clearTimeout(this.#debounceTimer);
    }

    if (query.length < 2) {
      this.#showDefaultContent();
      this.#previousQuery = '';
      return;
    }

    if (query === this.#previousQuery) return;

    this.#debounceTimer = setTimeout(() => {
      this.#search(query);
    }, 300);
  }

  /**
   * Handles keydown events on the search field.
   * @param {KeyboardEvent} event
   */
  handleKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
    }
  }

  /**
   * Handles reset button click.
   */
  handleReset() {
    if (this.refs.input) {
      this.refs.input.value = '';
      this.refs.input.focus();
    }
    this.#previousQuery = '';
    this.#updateResetButton();
    this.#showDefaultContent();
  }

  /**
   * Handles form submit.
   * Forces canonical search route and prevents empty submissions.
   * @param {SubmitEvent} event
   */
  handleSubmit(event) {
    const form = this.refs.form;
    const input = this.refs.input;
    if (!form || !input) return;

    const query = input.value.trim();
    if (query.length === 0) {
      event.preventDefault();
      return;
    }

    const searchUrl = Theme?.routes?.search_url || '/search';
    form.setAttribute('action', searchUrl);
  }

  #updateResetButton() {
    const hasValue = this.refs.input?.value.trim().length > 0;
    if (this.refs.resetButton) {
      this.refs.resetButton.hidden = !hasValue;
    }
  }

  #showDefaultContent() {
    if (this.refs.defaultContent) {
      this.refs.defaultContent.hidden = false;
    }
    if (this.refs.noResults) {
      this.refs.noResults.hidden = true;
    }
    if (this.refs.loading) {
      this.refs.loading.hidden = true;
    }

    // Remove search results but keep default content
    const searchResults = this.refs.results?.querySelector('#predictive-search-results');
    if (searchResults) {
      searchResults.remove();
    }
  }

  /**
   * Performs a predictive search.
   * @param {string} query
   */
  async #search(query) {
    this.#previousQuery = query;

    // Check cache first
    if (this.#cache.has(query)) {
      this.#renderResults(this.#cache.get(query) || '');
      return;
    }

    // Abort previous request
    this.#searchController?.abort();
    this.#searchController = new AbortController();

    this.#showLoading();

    try {
      const url = this.#buildSearchUrl(query);
      const response = await fetch(url, {
        signal: this.#searchController.signal,
      });

      if (!response.ok) {
        throw new Error(`Search failed: ${response.status}`);
      }

      const html = await response.text();
      this.#cache.set(query, html);
      this.#renderResults(html);
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        return;
      }
      console.error('Search error:', error);
      this.#showNoResults();
    }
  }

  /**
   * Builds the predictive search URL.
   * @param {string} query
   * @returns {string}
   */
  #buildSearchUrl(query) {
    const baseUrl = Theme?.routes?.predictive_search_url || '/search/suggest';
    const params = new URLSearchParams({
      q: query,
      'resources[type]': 'product,article',
      'resources[limit]': this.resultsLimit.toString(),
      'resources[limit_scope]': 'each',
      'resources[options][unavailable_products]': 'last',
      section_id: 'predictive-search',
    });

    return `${baseUrl}?${params.toString()}`;
  }

  #showLoading() {
    if (this.refs.loading) {
      this.refs.loading.hidden = false;
    }
    if (this.refs.defaultContent) {
      this.refs.defaultContent.hidden = true;
    }
    if (this.refs.noResults) {
      this.refs.noResults.hidden = true;
    }

    // Hide previous search results
    const searchResults = this.refs.results?.querySelector('#predictive-search-results');
    if (searchResults) {
      searchResults.remove();
    }
  }

  /**
   * Renders search results from HTML response.
   * @param {string} html
   */
  #renderResults(html) {
    if (this.refs.loading) {
      this.refs.loading.hidden = true;
    }

    // Parse the response HTML
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const resultsElement = doc.querySelector('#predictive-search-results');
    const dataScript = doc.querySelector('[data-predictive-search-data]');

    let hasResults = false;

    if (dataScript) {
      try {
        const data = JSON.parse(dataScript.textContent || '{}');
        hasResults = data.hasResults;
      } catch (e) {
        // Ignore JSON parse errors
      }
    }

    // Remove previous search results
    const existingResults = this.refs.results?.querySelector('#predictive-search-results');
    if (existingResults) {
      existingResults.remove();
    }

    if (resultsElement && hasResults) {
      // Hide default content
      if (this.refs.defaultContent) {
        this.refs.defaultContent.hidden = true;
      }
      if (this.refs.noResults) {
        this.refs.noResults.hidden = true;
      }

      // Insert new results
      this.refs.results?.insertAdjacentHTML('beforeend', resultsElement.outerHTML);
    } else {
      this.#showNoResults();
    }
  }

  #showNoResults() {
    if (this.refs.loading) {
      this.refs.loading.hidden = true;
    }
    if (this.refs.defaultContent) {
      this.refs.defaultContent.hidden = true;
    }
    if (this.refs.noResults) {
      this.refs.noResults.hidden = false;
    }

    // Remove any existing search results
    const searchResults = this.refs.results?.querySelector('#predictive-search-results');
    if (searchResults) {
      searchResults.remove();
    }
  }
}

if (!customElements.get('search-modal')) {
  customElements.define('search-modal', SearchModal);
}

export { SearchModal };
